import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import ModalComponent from "../ModalComponent";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import googleLogo from "../../assets/Google__G__logo.svg.png";
import SERVER_URL from "../../config/server";
import Cookies from "js-cookie";
import useAuthStore from "../../store/useAuthStore";

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [modalShow, setModalShow] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      Cookies.set("token", token, { expires: 7 });

      checkAuth();

      toast.success("Signup successful!");

      setTimeout(() => {
        navigate("/");
      }, 1200);
      return;
    }

    if (Cookies.get("token")) {
      navigate("/");
    }
  }, [checkAuth, navigate]);

  const handleGoogleSignUp = () => {
    const popup = window.open(
       `${SERVER_URL}/auth/google`,
       '_blank',
       'width=500,height=600'
     );
   
     const listener = (event) => {
        if (event.origin !== SERVER_URL) return;
    
        const { token } = event.data;
        if (token) {

          popup.close();
          Cookies.set("token", token, { expires: 7 });
          checkAuth();
          navigate("/");
          window.removeEventListener('message', listener); // clean up
        }
      };
    
      window.addEventListener('message', listener);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!isEmailVerified) {
      setError("Please verify your email first.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post("/user/signup", {
        name,
        email,
        password,
      });

      if (response.status === 201) {
        toast.success("Signup Successful!");
        setTimeout(() => navigate("/login"), 1500);
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleVerifyEmail = async () => {
    if (!email) {
      setError("Email is required.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      const response = await axios.post("/otp/send-otp", { email });

      if (response.status === 200) {
        setOtpSent(true);
        setModalShow(true);
        setTimer(60);
        toast.info("OTP Sent to your email");
      }
    } catch (error) {
      setError("Failed to send OTP.");
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center mt-20 text-white px-4">
      <ToastContainer position="top-right" autoClose={2000} theme="dark" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-gray-900 p-8 rounded-xl shadow-lg max-w-md w-full text-center"
      >
        <h2 className="text-3xl font-extrabold text-blue-500 mb-6">
          Sign Up
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col">

          {/* Name */}
          <div className="mb-4 text-left">
            <label className="block mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full p-3 bg-gray-800 border border-gray-600 rounded-md"
              placeholder="Enter your name"
            />
          </div>

          {/* Email + OTP */}
          <div className="mb-4 text-left">
            <label className="block mb-2">Email</label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={email}
                disabled={isEmailVerified}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 p-3 bg-gray-800 border border-gray-600 rounded-md"
                placeholder="Enter email"
              />

              {!isEmailVerified && (
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  className="bg-blue-600 px-3 py-2 rounded-md"
                >
                  Send OTP
                </button>
              )}

              {isEmailVerified && (
                <span className="text-green-400 font-medium">Verified ✔️</span>
              )}
            </div>

            <ModalComponent
              show={modalShow}
              email={email}
              onHide={() => setModalShow(false)}
              timer={timer}
              otpsent={otpSent}
              forgotPassword={false}
              onStatusChange={(status) => setIsEmailVerified(status)}
            />
          </div>

          {/* Password */}
          <div className="mb-4 text-left">
            <label className="block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 bg-gray-800 border border-gray-600 rounded-md"
              placeholder="Enter password"
            />
          </div>

          {/* Confirm Password */}
          <div className="mb-4 text-left">
            <label className="block mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full p-3 bg-gray-800 border border-gray-600 rounded-md"
              placeholder="Confirm password"
            />
          </div>

          {error && <p className="text-red-500 mb-4">{error}</p>}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 py-3 rounded-md font-bold hover:bg-blue-700 transition mb-4 flex items-center justify-center"
          >
            {isLoading ? "Signing Up..." : "Sign Up"}
          </button>

          {/* Login Link */}
          <p className="text-gray-400 text-sm mb-4">
            Already have an account?
            <Link to="/login" className="text-blue-500 ml-1">
              Login
            </Link>
          </p>

          <div className="flex items-center justify-center mb-4">
            <div className="w-1/4 h-px bg-gray-500"></div>
            <span className="mx-2 text-gray-400">OR</span>
            <div className="w-1/4 h-px bg-gray-500"></div>
          </div>

          {/* Google Signup */}
          <button
            onClick={handleGoogleSignUp}
            className="flex items-center justify-center w-full py-3 border border-gray-600 rounded hover:bg-gray-800 transition"
          >
            <img src={googleLogo} alt="Google" className="w-5 mr-3" />
            Sign up with Google
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default Signup;
